import {safelySerializeJSON} from './safelySerializeJSON';

const project: string | undefined = process.env.GOOGLE_CLOUD_PROJECT;

// https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#LogSeverity
enum Severity {
    DEFAULT = 0,
    DEBUG = 100,
    INFO = 200,
    WARNING = 400,
    ERROR = 500,
    CRITICAL = 600,
}

enum SeverityStrings {
    DEFAULT = 'DEFAULT',
    DEBUG = 'DEBUG',
    INFO = 'INFO',
    WARNING = 'WARNING',
    ERROR = 'ERROR',
    CRITICAL = 'CRITICAL',
}

const minLogSeverity = Severity[process.env.MIN_LOG_SEVERITY || 'DEFAULT'];

const consoleFuncMapping: { [key in SeverityStrings]: string } = {
    [SeverityStrings.DEFAULT]: 'log',
    [SeverityStrings.DEBUG]: 'debug',
    [SeverityStrings.INFO]: 'info',
    [SeverityStrings.WARNING]: 'warn',
    [SeverityStrings.ERROR]: 'error',
    [SeverityStrings.CRITICAL]: 'error',
};

interface Identifier {
    component?: string;
    debug?: object;
    trace?: string;
    workerId?: number;
    localLogReqId?: number;
}

/**
 * Used for Google Cloud Logging, which support log severity.
 */
function structuredLog(
    identifier: Identifier, severity: SeverityStrings, loggable: any): void {
    if (Severity[severity] < minLogSeverity) {
        return;
    }
    const entry: { [key: string]: any } = {
        severity,
        component: identifier.component,
        time: new Date().toISOString(),
    };
    if (identifier.trace) {
        entry['logging.googleapis.com/trace'] = `projects/${project}/traces/${identifier.trace}`;
    }

    if (loggable.message) {
        loggable.message = loggable.message.map(safelySerializeJSON).join(' ');
    }

    Object.assign(entry, loggable);

    return console[consoleFuncMapping[severity]](
        safelySerializeJSON(entry));
}

interface ConsoleLogIdentifier extends Identifier {
    workerId: number;
    localLogReqId: number;
}

/**
 * Used for dev server, which doesn't indicate log severity.
 */
function serverConsoleLog(
    identifier: ConsoleLogIdentifier,
    severity: SeverityStrings, loggable: any): void {
    if (Severity[severity] < minLogSeverity) {
        return;
    }
    const prefix: string = `[${severity}]req:${identifier.localLogReqId || -1}${identifier.component ? `:${identifier.component}` : ''}`;
    const consoleFunc = console[consoleFuncMapping[severity]];
    if (loggable.message) {
        const {message, ...otherLoggable} = loggable;
        if (identifier.debug) {
            if (identifier.component && message[0].includes(
                identifier.component)) {
                message[0] = message[0].replace(identifier.component, prefix);
            } else {
                message[0] = `${prefix} ${message[0]}`;
            }
        } else {
            message.unshift(prefix);
        }
        return consoleFunc(...message, otherLoggable);
    }
    return consoleFunc(prefix, loggable);
}

interface BrowserConsoleLogIdentifier extends Identifier {
    localLogReqId: number;
}

/**
 * Used for browser DevTools, which support log severity.
 */
function browserConsoleLog(identifier: BrowserConsoleLogIdentifier,
    severity: SeverityStrings, loggable: any): void {
    if (Severity[severity] < minLogSeverity) {
        return;
    }
    const prefix: string = `req:${identifier.localLogReqId || -1}${identifier.component ? `:${identifier.component}` : ''}`;
    const consoleFunc = console[consoleFuncMapping[severity]];
    if (loggable.message) {
        const {message, ...otherLoggable} = loggable;
        if (identifier.debug) {
            if (identifier.component && message[0].includes(
                identifier.component)) {
                message[0] = message[0].replace(identifier.component, prefix);
            } else {
                message[0] = `${prefix} ${message[0]}`;
            }
        } else {
            message.unshift(prefix);
        }
        return consoleFunc(...message, otherLoggable);
    }
    return consoleFunc(prefix, loggable);
}

type LoggingFunction = (
    identifier: any, severity: SeverityStrings, loggable: any) => void;

const baseLoggingFunction: LoggingFunction = project ?
    structuredLog : process.env.BROWSER ? browserConsoleLog : serverConsoleLog;
function loggingFunction(
    identifier: any, severity: SeverityStrings, loggable: any): void {
    if (Severity[severity] < minLogSeverity) {
        return;
    }
    baseLoggingFunction(identifier, severity, loggable);
}

type HttpRequestStructredLog = {
    status: number,
    responseSize: string,
    latency: string,
};

export class StructuredConsole {
    identifier: Identifier;

    constructor(identifier: Identifier) {
        this.identifier = identifier;
    }

    baseLog(severity: SeverityStrings, loggable: any): void {
        return loggingFunction(this.identifier, severity, loggable);
    }

    log(...message: any[]): void {
        return this.baseLog(SeverityStrings.DEFAULT, {message});
    }

    debug(...message: any[]): void {
        return this.baseLog(SeverityStrings.DEBUG, {message});
    }

    info(...message: any[]): void {
        return this.baseLog(SeverityStrings.INFO, {message});
    }

    warn(...message: any[]): void {
        return this.baseLog(SeverityStrings.WARNING, {message});
    }

    error(...message: any[]): void {
        return this.baseLog(SeverityStrings.ERROR, {message});
    }

    critical(...message: any[]): void {
        return this.baseLog(SeverityStrings.CRITICAL, {message});
    }

    baseHttp(severity: SeverityStrings,
        httpRequest: object, ...message: any[]): void {
        return this.baseLog(severity, {httpRequest, message});
    }

    httpDone(httpRequest: HttpRequestStructredLog, ...message: any[]): void {
        const ok: boolean = httpRequest.status && httpRequest.status < 400;
        return this.baseHttp(
            ok ? SeverityStrings.INFO : SeverityStrings.WARNING,
            httpRequest,
            ...message,
        );
    }

    httpError(httpRequest: object, ...message: any[]): void {
        return this.baseHttp(SeverityStrings.ERROR, httpRequest, ...message);
    }

    addIdentifier(newIdentifier: Identifier): StructuredConsole {
        return new StructuredConsole({...this.identifier, ...newIdentifier});
    }

    extendDebug(debugInstance: any, namespace: string): any {
        const extendedDebug = debugInstance.extend(namespace);
        const newObj = new StructuredConsole({
            ...this.identifier,
            component: extendedDebug.namespace,
            debug: extendedDebug,
        });
        extendedDebug.log = newObj.debug.bind(newObj);
        return extendedDebug;
    }
}
